import { escapeHtml, type BodyFormat } from './body';
import { MERGE_VARIABLES, type MergeVariable } from './constants';

/**
 * Template merge for the batch composer (PRD §F3).
 *
 * Two rules the PRD makes non-negotiable, both enforced here rather than in the
 * UI so the send endpoint cannot be talked out of them:
 *
 *  - An unresolved variable blocks the send. A florist receiving
 *    "Hi {{vendor_name}}" is worse than no email at all, and the failure is
 *    invisible to the planner once it has left the outbox.
 *  - An unknown variable is an error, not a passthrough. `{{vendorname}}` is a
 *    typo, and silently rendering it as literal text ships the typo.
 */

export type MergeContext = Partial<Record<MergeVariable, string | null | undefined>>;

const TOKEN = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g;

export interface MergeIssue {
  variable: string;
  kind: 'unknown' | 'empty';
}

export interface MergeResult {
  text: string;
  issues: MergeIssue[];
}

export function isKnown(name: string): name is MergeVariable {
  return (MERGE_VARIABLES as readonly string[]).includes(name);
}

export interface TokenSpan {
  /** Index of the opening `{` in the source text. */
  start: number;
  /** Index one past the closing `}`. */
  end: number;
  /** The variable name, trimmed — what `renderTemplate` would look up. */
  name: string;
}

/**
 * Where every token sits in the text, in order.
 *
 * The template editor paints a chip over each of these, and a highlighter that
 * disagreed with the renderer about what counts as a token would be worse than
 * no highlighter at all — it would draw a friendly chip on something the send
 * endpoint then refuses. So both read the same pattern from here.
 */
export function tokenSpans(text: string): TokenSpan[] {
  const spans: TokenSpan[] = [];
  for (const match of text.matchAll(TOKEN)) {
    const index = match.index ?? 0;
    spans.push({
      start: index,
      end: index + match[0].length,
      name: (match[1] ?? '').trim(),
    });
  }
  return spans;
}

/**
 * Merges into a body of the given format.
 *
 * The format is not decoration. A merged value is *data* — a couple called
 * "Tom & Ana", a venue named "Hart & Sons", a time written "5 <> 11" — and
 * dropped into an HTML body unescaped it stops being data: the ampersand
 * becomes a malformed entity and the angle bracket eats the rest of the
 * sentence in the vendor's client. The planner sees none of this, because the
 * editor they typed in escapes on their behalf and only the *merge* reaches
 * the raw string.
 */
export function renderTemplate(
  template: string,
  context: MergeContext,
  format: BodyFormat = 'text',
): MergeResult {
  const issues: MergeIssue[] = [];
  const seen = new Set<string>();
  const encode = format === 'html' ? escapeHtml : (v: string) => v;

  const text = template.replace(TOKEN, (_match, rawName: string) => {
    const name = rawName.trim();

    if (!isKnown(name)) {
      if (!seen.has(`unknown:${name}`)) {
        seen.add(`unknown:${name}`);
        issues.push({ variable: name, kind: 'unknown' });
      }
      // Leave the token intact so the preview shows the planner exactly which
      // text is unresolved, rather than a confusing blank.
      return `{{${name}}}`;
    }

    const value = context[name];
    if (value === undefined || value === null || value.trim() === '') {
      if (!seen.has(`empty:${name}`)) {
        seen.add(`empty:${name}`);
        issues.push({ variable: name, kind: 'empty' });
      }
      return `{{${name}}}`;
    }

    return encode(value);
  });

  return { text, issues };
}

/** Every distinct variable a template references, known or not, in first-use order. */
export function extractVariables(template: string): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const match of template.matchAll(TOKEN)) {
    const name = (match[1] ?? '').trim();
    if (seen.has(name)) continue;
    seen.add(name);
    out.push(name);
  }
  return out;
}

/**
 * Rotates through a template's opening variants so a ten-message batch is not
 * ten byte-identical bodies. Deterministic on index rather than random: a
 * preview and the subsequent send must agree on what the planner approved.
 *
 * `{{opening}}` is handled here rather than in `renderTemplate` because it is a
 * body-assembly concern, not a merge variable — it never appears in
 * MERGE_VARIABLES and the planner never fills it in.
 */
export function applyOpeningVariant(body: string, variants: readonly string[], index: number): string {
  if (variants.length === 0) return body.replace(/\{\{\s*opening\s*\}\}\n?/g, '');
  const variant = variants[index % variants.length] ?? '';
  return body.replace(/\{\{\s*opening\s*\}\}/g, variant);
}

/**
 * Deterministic stagger offsets, in seconds, for a batch of `count` messages.
 *
 * The first message goes immediately; each subsequent one lands 20-60s after
 * the previous. Jitter is seeded from the batch id so a preview and the actual
 * schedule agree, and so a test can assert exact offsets.
 */
export function staggerOffsets(
  count: number,
  seed: string,
  minSeconds = 20,
  maxSeconds = 60,
): number[] {
  // FNV-1a: small, dependency-free, and good enough to decorrelate offsets.
  let hash = 0x811c9dc5;
  for (let i = 0; i < seed.length; i++) {
    hash ^= seed.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }

  const offsets: number[] = [];
  let cumulative = 0;
  for (let i = 0; i < count; i++) {
    offsets.push(cumulative);
    hash ^= hash << 13;
    hash >>>= 0;
    hash ^= hash >>> 17;
    hash ^= hash << 5;
    hash >>>= 0;
    const span = maxSeconds - minSeconds + 1;
    cumulative += minSeconds + (hash % span);
  }
  return offsets;
}
